import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Tabs, 
  Tab, 
  Table, 
  Spinner, 
  Accordion, 
  Row, 
  Col, 
  Alert, 
  Badge,
  Card
} from 'react-bootstrap';
import { 
  FaShieldAlt, 
  FaEye, 
  FaEdit, 
  FaCog,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import { 
  BsInfoCircle, 
  BsFileText
} from 'react-icons/bs';

import rbacService from '../../../services/rbacService';
import dynamicFormsService from '../../../services/dynamicFormsService';

/**
 * ViewPermissionsModal
 * 
 * Read-only modal for viewing role permissions
 * Shows both system permissions and form permissions
 * 
 * Props:
 *  - show (boolean)
 *  - roleId (string) - The role ID to view permissions for
 *  - onHide () => void
 */
const ViewPermissionsModal = ({ show, roleId, onHide }) => {
  const [loading, setLoading] = useState(true);
  const [systemPermissions, setSystemPermissions] = useState({}); // { category: Permission[] }
  const [formList, setFormList] = useState([]); // summary list of forms
  const [grantedPermissions, setGrantedPermissions] = useState(new Set()); // Set of granted permission_ids
  const [roleName, setRoleName] = useState('');
  const [activeTab, setActiveTab] = useState('system');
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [stats, setStats] = useState({
    totalPermissions: 0,
    grantedPermissions: 0,
    grantedSystemPermissions: 0,
    grantedFormPermissions: 0,
    totalForms: 0,
    totalSystemPermissions: 0
  });

  // Calculate statistics
  const calculateStats = () => {
    const totalForms = formList.length;
    const systemPermsCount = Object.values(systemPermissions).flat().length;
    const totalFormPerms = totalForms * 2; // 2 perms per form (view, edit)
    const totalPerms = totalFormPerms + systemPermsCount;
    
    // Count granted system permissions
    const grantedSystemPerms = [...grantedPermissions].filter(pid => 
      !pid.startsWith('forms.')
    ).length;
    
    // Count granted form permissions 
    const grantedFormPerms = [...grantedPermissions].filter(pid => 
      pid.startsWith('forms.')
    ).length;
    
    setStats({
      totalPermissions: totalPerms,
      grantedPermissions: grantedPermissions.size,
      grantedSystemPermissions: grantedSystemPerms,
      grantedFormPermissions: grantedFormPerms,
      totalForms: totalForms,
      totalSystemPermissions: systemPermsCount
    });
  };

  // Fetch data on open
  useEffect(() => {
    if (!show || !roleId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch ALL permissions FIRST to build UUID → code mapping
        let allPerms = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await rbacService.getPermissions({ page, page_size: 1000 });
          allPerms = allPerms.concat(res.data || []);
          hasMore = res.hasMore || false;
          page = (res.page || page) + 1;
        }

        // Build UUID to Code mapping
        const idToCodeMap = {};
        allPerms.forEach((perm) => {
          const permId = perm.id; // UUID
          const permCode = perm.code || perm.permission_id; // Human-readable code
          if (permId && permCode) {
            idToCodeMap[permId] = permCode;
          }
        });

        // 2. Fetch role (to get current permissions and role name)
        const roleRes = await rbacService.getRoleById(roleId);
        const role = roleRes?.data || roleRes;

        // Convert role permissions from UUIDs to codes
        const currentPerms = new Set((role?.permissions || []).map((p) => {
          const permUUID = p.permission_id || p.id;
          const permCode = idToCodeMap[permUUID] || permUUID; // UUID → Code conversion
          return permCode;
        }));

        setGrantedPermissions(currentPerms);
        setRoleName(role?.name || role?.display_name || 'Unknown Role');

        // 3. Separate system permissions (non-form permissions)
        const systemMap = {};
        allPerms.forEach((perm) => {
          // Skip form-specific permissions - they'll be handled in form tab
          const permCode = perm.code || perm.permission_id || perm.id || '';
          if (perm.category === 'forms' || /^forms\./i.test(permCode)) {
            return;
          }

          // Normalize permission to use code as permission_id
          const normalizedPerm = {
            ...perm,
            permission_id: permCode // Use code for consistent matching
          };

          const cat = perm.category || perm.module || 'general';
          if (!systemMap[cat]) systemMap[cat] = [];
          systemMap[cat].push(normalizedPerm);
        });
        setSystemPermissions(systemMap);
        setPermissionCategories(Object.keys(systemMap));

        // 4. Fetch form summary list
        const formRes = await dynamicFormsService.getForms({ summary: true, page_size: 500 });
        setFormList(formRes.data || []);
      } catch (err) {
        console.error('Error fetching permissions:', err);
        // Set default values to prevent UI errors
        setSystemPermissions({});
        setFormList([]);
        setGrantedPermissions(new Set());
        setRoleName('Unknown Role');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [show, roleId]);

  // Calculate stats whenever granted permissions or form list changes
  useEffect(() => {
    if (formList.length > 0 || Object.keys(systemPermissions).length > 0) {
      calculateStats();
    }
  }, [grantedPermissions, formList, systemPermissions]);

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} dialogClassName="modal-xl">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaEye className="text-info" />
          View Permissions - {roleName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
            <div className="text-center">
              <Spinner animation="border" variant="info" />
              <p className="mt-3 text-muted">Loading permissions...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Permission Statistics */}
            <Row className="mb-4">
              <Col md={12}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <Row>
                      <Col md={3} className="text-center">
                        <h5 className="mb-1 text-primary">{stats.grantedPermissions}</h5>
                        <small className="text-muted">Total Granted</small>
                      </Col>
                      <Col md={3} className="text-center">
                        <h5 className="mb-1 text-warning">{stats.grantedSystemPermissions}</h5>
                        <small className="text-muted">System Permissions</small>
                      </Col>
                      <Col md={3} className="text-center">
                        <h5 className="mb-1 text-success">{stats.grantedFormPermissions}</h5>
                        <small className="text-muted">Form Permissions</small>
                      </Col>
                      <Col md={3} className="text-center">
                        <h5 className="mb-1 text-info">{stats.totalForms}</h5>
                        <small className="text-muted">Available Forms</small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Main Permission Tabs */}
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
              <Tab eventKey="system" title={<><FaCog className="me-2" />System Permissions</>}>
                <div className="pt-3">
                  {Object.keys(systemPermissions).length === 0 ? (
                    <Alert variant="info" className="text-center">
                      <BsInfoCircle className="me-2" />
                      No system permissions found for this role.
                    </Alert>
                  ) : (
                    <Accordion alwaysOpen>
                      {Object.entries(systemPermissions).map(([category, perms]) => {
                        const grantedCount = perms.filter(p => grantedPermissions.has(p.permission_id || p.code || p.id)).length;
                        const variant = grantedCount === 0 ? 'light' : grantedCount === perms.length ? 'success' : 'warning';
                        
                        return (
                          <Accordion.Item eventKey={category} key={category}>
                            <Accordion.Header>
                              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                <span>{category.replace(/_/g, ' ').toUpperCase()}</span>
                                <Badge bg={variant}>
                                  {grantedCount}/{perms.length}
                                </Badge>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              <Table hover responsive size="sm">
                                <thead className="table-light">
                                  <tr>
                                    <th>Permission</th>
                                    <th>Description</th>
                                    <th className="text-center">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {perms.map((perm) => {
                                    const permissionId = perm.permission_id || perm.code || perm.id;
                                    const isGranted = grantedPermissions.has(permissionId);
                                    return (
                                      <tr key={permissionId} className={isGranted ? 'table-success' : ''}>
                                        <td>
                                          <strong>{perm.name || perm.display_name}</strong>
                                          <br />
                                          <small className="text-muted">{permissionId}</small>
                                        </td>
                                        <td>
                                          <small className="text-muted">{perm.description || 'No description available'}</small>
                                        </td>
                                        <td className="text-center">
                                          {isGranted ? (
                                            <Badge bg="success">
                                              <FaCheck className="me-1" />
                                              Granted
                                            </Badge>
                                          ) : (
                                            <Badge bg="secondary">
                                              <FaTimes className="me-1" />
                                              Not Granted
                                            </Badge>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </Accordion.Body>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                  )}
                </div>
              </Tab>

              <Tab eventKey="forms" title={<><BsFileText className="me-2" />Form Permissions</>}>
                <div className="pt-3">
                  <Alert variant="info" className="mb-3">
                    <BsInfoCircle className="me-2" />
                    <strong>Form Permissions:</strong> Shows access and editing rights for each form. Only forms with granted permissions are displayed.
                  </Alert>
                  
                  {formList.length === 0 ? (
                    <Alert variant="warning" className="text-center">
                      <BsInfoCircle className="me-2" />
                      No forms available in the system.
                    </Alert>
                  ) : (
                    <>
                      <Table hover responsive size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Form Name</th>
                            <th>Department</th>
                            <th className="text-center">
                              <FaEye className="me-1" />
                              Access
                            </th>
                            <th className="text-center">
                              <FaEdit className="me-1" />
                              Edit
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {formList.map((form) => {
                            const viewPid = `forms.${form._id || form.form_id}.view`;
                            const editPid = `forms.${form._id || form.form_id}.edit`;
                            const hasAccess = grantedPermissions.has(viewPid);
                            const hasEdit = grantedPermissions.has(editPid);
                            
                            // Only show forms with at least one permission
                            if (!hasAccess && !hasEdit) return null;
                            
                            return (
                              <tr key={form._id || form.form_id} className="table-success">
                                <td>
                                  <strong>{form.name}</strong>
                                  <div className="mt-1">
                                    {hasAccess && <Badge bg="info" size="sm" className="me-1">Access</Badge>}
                                    {hasEdit && <Badge bg="warning" size="sm">Edit</Badge>}
                                  </div>
                                </td>
                                <td>
                                  <small className="text-muted">{form.department_name || form.departmentName || 'Unknown'}</small>
                                </td>
                                <td className="text-center">
                                  {hasAccess ? (
                                    <Badge bg="success">
                                      <FaCheck className="me-1" />
                                      Granted
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary">
                                      <FaTimes className="me-1" />
                                      Not Granted
                                    </Badge>
                                  )}
                                </td>
                                <td className="text-center">
                                  {hasEdit ? (
                                    <Badge bg="success">
                                      <FaCheck className="me-1" />
                                      Granted
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary">
                                      <FaTimes className="me-1" />
                                      Not Granted
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          }).filter(Boolean)}
                        </tbody>
                      </Table>
                      
                      {formList.every(form => {
                        const viewPid = `forms.${form._id || form.form_id}.view`;
                        const editPid = `forms.${form._id || form.form_id}.edit`;
                        return !grantedPermissions.has(viewPid) && !grantedPermissions.has(editPid);
                      }) && (
                        <Alert variant="info" className="text-center">
                          <BsInfoCircle className="me-2" />
                          This role has no form permissions assigned.
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          <button 
            className="btn btn-secondary"
            onClick={onHide}
          >
            Close
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewPermissionsModal;